"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { Stream } from "@/lib/api/types";

export interface StreamList {
  items: Stream[];
  total: number;
}

const REFRESH_MS = 60_000;

/**
 * Keeps a list of live streams current while the page is open: ended streams
 * drop out, new ones join at the end. Cards that stay keep their place so the
 * row doesn't reshuffle under the visitor's cursor.
 */
/** `params`: the filters as a query string (lib/stream-filters.ts streamParams), empty for every live stream. */
export function useLiveStreams(initial: StreamList, limit: number, params = "") {
  const [list, setList] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const limitRef = useRef(limit);
  const lastRef = useRef(0);
  const busy = useRef(false);

  const fetchList = useCallback(async (n: number) => {
    const res = await fetch(`/api/streams?limit=${n}${params ? `&${params}` : ""}`, { cache: "no-store" });
    if (!res.ok) throw new Error(String(res.status));
    const next: StreamList = await res.json();
    lastRef.current = Date.now();
    setList((prev) => {
      const fresh = new Map(next.items.map((s) => [s.id, s]));
      const kept = prev.items.flatMap((s) => fresh.get(s.id) ?? []);
      const seen = new Set(kept.map((s) => s.id));
      return { items: [...kept, ...next.items.filter((s) => !seen.has(s.id))], total: next.total };
    });
  }, [params]);

  /** Grow the list by `step` (infinite scroll on the home page). */
  const loadMore = useCallback(
    async (step: number) => {
      if (busy.current) return;
      busy.current = true;
      setLoading(true);
      setError(false);
      try {
        limitRef.current += step;
        await fetchList(limitRef.current);
      } catch {
        limitRef.current -= step;
        setError(true);
      } finally {
        busy.current = false;
        setLoading(false);
      }
    },
    [fetchList],
  );

  useEffect(() => {
    lastRef.current = Date.now();
    const refresh = () => {
      if (document.visibilityState !== "visible" || busy.current || Date.now() - lastRef.current < REFRESH_MS / 2) return;
      fetchList(limitRef.current).catch(() => {});
    };
    const timer = window.setInterval(refresh, REFRESH_MS);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [fetchList]);

  return { ...list, hasMore: list.items.length < list.total, loading, error, loadMore };
}
